import { useCallback, useEffect, useRef, useState } from 'react'
import { useTenant } from '../../../contexts/TenantContext'
import { tenantUsesBlissBasicBackOffice } from '../../../config/blissBasicHost'
import {
  BLISS_BASIC_OPTION_CATEGORIES,
  type BlissBasicOptionCategoryId,
} from '../../../config/blissBasicOptionCategories'
import {
  deleteBlissBasicGalleryPhoto,
  deleteBlissBasicProduct,
  fetchBlissBasicGalleryPhotos,
  fetchBlissBasicProducts,
  parseLandingSettings,
  photoPublicUrl,
  updateBlissBasicGalleryPhoto,
  uploadBlissBasicGalleryPhoto,
  upsertBlissBasicProduct,
  type BlissBasicGalleryPhotoRow,
  type BlissBasicProductRow,
} from '../../../services/blissBasicCatalogAdminService'
import BlissBasicConfiguratorAdminSection from './BlissBasicConfiguratorAdminSection'
import type { BlissBasicConfiguratorStepCopy } from '../../../config/blissBasicCatalog'
import './BlissBasicProductsTab.css'

export default function BlissBasicProductsTab() {
  const { tenantId, tenantSlug, settings, updateSettings, isPlatformAdmin } = useTenant()
  const canEdit = isPlatformAdmin
  const isBliss = tenantUsesBlissBasicBackOffice(tenantSlug)

  const [products, setProducts] = useState<BlissBasicProductRow[]>([])
  const [photos, setPhotos] = useState<BlissBasicGalleryPhotoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [savingLanding, setSavingLanding] = useState(false)
  const [landingStatus,setLandingStatus] = useState('')
  const [editPhoto, setEditPhoto] = useState<BlissBasicGalleryPhotoRow | null>(null)
  const [editProduct, setEditProduct] = useState<BlissBasicProductRow | 'new' | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadCategory, setUploadCategory] = useState<BlissBasicOptionCategoryId>('bed_type')

  const landing = parseLandingSettings(settings as Record<string, unknown>)
  const [landingForm, setLandingForm] = useState(landing)
  const configuratorStepCopy =
    ((settings as Record<string, unknown>).blissBasicConfiguratorStepCopy as BlissBasicConfiguratorStepCopy) ?? {}

  useEffect(() => {
    setLandingForm(parseLandingSettings(settings as Record<string, unknown>))
  }, [settings])

  const reload = useCallback(async () => {
    if (!tenantId) return
    setLoading(true)
    const [p, ph] = await Promise.all([
      fetchBlissBasicProducts(tenantId),
      fetchBlissBasicGalleryPhotos(tenantId),
    ])
    setProducts(p)
    setPhotos(ph)
    setLoading(false)
  }, [tenantId])

  useEffect(() => {
    void reload()
  }, [reload])

  if (!isBliss) {
    return (
      <div className="bbp-tab">
        <p>Products catalog is available on Bliss tenant accounts only.</p>
      </div>
    )
  }

  if (!canEdit) {
    return (
      <div className="bbp-tab bbp-denied">
        <h2>Products</h2>
        <p>Only the Bliss owner can manage public photos, retail prices, and website settings. Use Catalog in your partner dashboard to add a product.</p>
      </div>
    )
  }

  const saveLanding = async () => {
    setSavingLanding(true)
    setLandingStatus('')
    try {
    await updateSettings({
      blissBasicLanding: {
        startingPricePrefix: landingForm.startingPricePrefix,
        startingPriceUsd: landingForm.startingPriceUsd,
        investmentNote: landingForm.investmentNote,
      },
    })
    setLandingStatus('Landing settings saved.')
    } catch { setLandingStatus('Could not save landing settings. Please try again.') }
    finally { setSavingLanding(false) }
  }

  const onUploadFiles = async (files: FileList | null) => {
    if (!tenantId || !files?.length) return
    const maxSort = photos.reduce((m, p) => Math.max(m, p.sort_order), 0)
    for (let i = 0; i < files.length; i++) {
      await uploadBlissBasicGalleryPhoto(tenantId, files[i], {
        sort_order: maxSort + 1 + i,
        option_category: uploadCategory,
      })
    }
    await reload()
  }

  const savePhotoEdit = async () => {
    if (!tenantId || !editPhoto) return
    await updateBlissBasicGalleryPhoto(tenantId, editPhoto.id, {
      title: editPhoto.title,
      description: editPhoto.description,
      focal_point: editPhoto.focal_point,
      price_usd: editPhoto.price_usd,
      product_id: editPhoto.product_id,
      active: editPhoto.active,
      is_hero: editPhoto.is_hero,
      option_category: editPhoto.option_category ?? null,
      in_stock: editPhoto.in_stock ?? true,
    })
    setEditPhoto(null)
    await reload()
  }

  const saveProduct = async () => {
    if (!tenantId || !editProduct || editProduct === 'new') {
      if (!tenantId || editProduct !== 'new') return
      const name = (document.getElementById('bbp-new-product-name') as HTMLInputElement)?.value?.trim()
      if (!name) return
      await upsertBlissBasicProduct(tenantId, { name, sort_order: products.length })
      setEditProduct(null)
      await reload()
      return
    }
    await upsertBlissBasicProduct(tenantId, editProduct)
    setEditProduct(null)
    await reload()
  }

  const toggleStock = (ph: BlissBasicGalleryPhotoRow) => {
    if (!tenantId) return
    const inStock = ph.in_stock ?? true
    void updateBlissBasicGalleryPhoto(tenantId, ph.id, { in_stock: !inStock }).then(reload)
  }

  const renderPhotoCard = (ph: BlissBasicGalleryPhotoRow) => {
    const inStock = ph.in_stock ?? true
    return (
      <div key={ph.id} className={`bbp-card${inStock ? '' : ' bbp-card--out-of-stock'}`}>
        <img
          src={photoPublicUrl(ph.storage_path)}
          alt={ph.title ?? 'Gallery'}
          style={{ objectPosition: 'center' }}
        />
        <div className="bbp-card-body">
          <strong>{ph.title ?? 'Untitled'}</strong>
          {ph.focal_point ? <div>Focal: {ph.focal_point}</div> : null}
          {ph.price_usd != null ? <div>${Number(ph.price_usd).toLocaleString()}</div> : null}
          {ph.is_hero ? <div>★ Hero image</div> : null}
          {!ph.active ? <div>Hidden from site</div> : null}
          <div className="bbp-stock-row">
            <span className={inStock ? 'bbp-stock bbp-stock--in' : 'bbp-stock bbp-stock--out'}>
              {inStock ? 'In stock' : 'Out of stock'}
            </span>
            <button type="button" className="bbp-btn bbp-btn-small" onClick={() => toggleStock(ph)}>
              {inStock ? 'Mark out of stock' : 'Mark in stock'}
            </button>
          </div>
          <div className="bbp-card-actions">
            <button type="button" className="bbp-btn" onClick={() => setEditPhoto({ ...ph })}>
              Edit
            </button>
            <button
              type="button"
              className="bbp-btn"
              onClick={() =>
                void updateBlissBasicGalleryPhoto(tenantId!, ph.id, { is_hero: true, option_category: null }).then(
                  reload,
                )
              }
            >
              Set hero
            </button>
            <button
              type="button"
              className="bbp-btn bbp-btn-danger"
              onClick={() => {
                if (confirm('Delete this photo?')) {
                  void deleteBlissBasicGalleryPhoto(tenantId!, ph).then(reload)
                }
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bbp-tab">
      <h2>Product catalog</h2>
      <p className="bbp-lede">
        Upload and edit photos, set prices, and manage packages for your landing on{' '}
        <strong>basic.blissbunkbeds.com</strong>. Save to publish.
      </p>

      <nav className="bbp-section-nav" aria-label="Catalog sections"><a href="#bbp-landing">Landing page</a><a href="#bbp-packages">Packages</a><a href="#bbp-configurator">Configurator</a><a href="#bbp-gallery">Photo library</a></nav>
      {loading && <p>Loading catalog…</p>}

      <section className="bbp-section" id="bbp-landing">
        <h3>Landing page pricing</h3>
        <div className="bbp-grid-2">
          <div className="bbp-field">
            <label htmlFor="bbp-prefix">Label</label>
            <input
              id="bbp-prefix"
              value={landingForm.startingPricePrefix}
              onChange={(e) =>
                setLandingForm((f) => ({ ...f, startingPricePrefix: e.target.value }))
              }
            />
          </div>
          <div className="bbp-field">
            <label htmlFor="bbp-price">Price (USD)</label>
            <input
              id="bbp-price"
              type="number"
              min={0}
              step={100}
              value={landingForm.startingPriceUsd}
              onChange={(e) =>
                setLandingForm((f) => ({
                  ...f,
                  startingPriceUsd: Number(e.target.value) || 0,
                }))
              }
            />
          </div>
          <div className="bbp-field" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="bbp-note">Subline under price</label>
            <input
              id="bbp-note"
              value={landingForm.investmentNote}
              onChange={(e) => setLandingForm((f) => ({ ...f, investmentNote: e.target.value }))}
            />
          </div>
        </div>
        <div className="bbp-actions">
          <button type="button" className="bbp-btn bbp-btn-primary" disabled={savingLanding} onClick={() => void saveLanding()}>
            {savingLanding ? 'Saving…' : 'Save landing settings'}
          </button>
          {landingStatus && <p role="status">{landingStatus}</p>}
        </div>
      </section>

      <section className="bbp-section" id="bbp-packages">
        <h3>Package bundles</h3>
        <div className="bbp-table-scroll">
          <table className="bbp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Price</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>
                    {p.price_label} {p.price_usd != null ? `$${Number(p.price_usd).toLocaleString()}` : '—'}
                  </td>
                  <td>{p.active ? 'Yes' : 'No'}</td>
                  <td>
                    <button type="button" className="bbp-btn" onClick={() => setEditProduct(p)}>
                      Edit
                    </button>{' '}
                    <button
                      type="button"
                      className="bbp-btn bbp-btn-danger"
                      onClick={() => {
                        if (confirm('Delete this package?')) void deleteBlissBasicProduct(tenantId!, p.id).then(reload)
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bbp-actions">
          <button type="button" className="bbp-btn bbp-btn-primary" onClick={() => setEditProduct('new')}>
            Add package
          </button>
        </div>
      </section>

      <div id="bbp-configurator">{tenantId && (
        <BlissBasicConfiguratorAdminSection
          tenantId={tenantId}
          stepCopy={configuratorStepCopy}
          onSaveStepCopy={async (copy) => {
            await updateSettings({ blissBasicConfiguratorStepCopy: copy })
          }}
          photos={photos}
          onReload={reload}
        />
      )}</div>

      <section className="bbp-section" id="bbp-gallery">
        <h3>Photo library · six-step picker</h3>
        <p className="bbp-lede" style={{ marginBottom: 12 }}>
          Assign each photo to a step on the landing options flow. Shoppers pick one image per step; out-of-stock
          options stay in your library but won&apos;t appear for customers.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={(e) => void onUploadFiles(e.target.files).then(() => {
            if (fileInputRef.current) fileInputRef.current.value = ''
          })}
        />
        <div className="bbp-upload-row">
          <div className="bbp-field">
            <label htmlFor="bbp-upload-cat">Upload into step</label>
            <select
              id="bbp-upload-cat"
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value as BlissBasicOptionCategoryId)}
            >
              {BLISS_BASIC_OPTION_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.step}. {c.subtitle} ({c.examples})
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="bbp-btn bbp-btn-primary" onClick={() => fileInputRef.current?.click()}>
            Upload to this step
          </button>
        </div>

        {BLISS_BASIC_OPTION_CATEGORIES.map((cat) => {
          const catPhotos = photos.filter((p) => (p.option_category ?? null) === cat.id)
          return (
            <div key={cat.id} className="bbp-cat-block">
              <h4>
                {cat.step}. {cat.subtitle}
                <span className="bbp-cat-examples">{cat.examples}</span>
              </h4>
              {catPhotos.length === 0 ? (
                <p className="bbp-empty-cat">No photos yet — upload above or move photos here in Edit.</p>
              ) : (
                <div className="bbp-gallery">
                  {catPhotos.map((ph) => renderPhotoCard(ph))}
                </div>
              )}
            </div>
          )
        })}

        <div className="bbp-cat-block">
          <h4>Hero &amp; gallery (not in wizard)</h4>
          <p className="bbp-cat-examples">No option step — landing hero only</p>
          <div className="bbp-gallery">
            {photos.filter((p) => !p.option_category).map((ph) => renderPhotoCard(ph))}
          </div>
        </div>
      </section>

      {editPhoto && (
        <div className="bbp-modal-backdrop" role="presentation" onClick={() => setEditPhoto(null)}>
          <div className="bbp-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Edit photo</h3>
            <div className="bbp-field">
              <label>Title</label>
              <input
                value={editPhoto.title ?? ''}
                onChange={(e) => setEditPhoto({ ...editPhoto, title: e.target.value })}
              />
            </div>
            <div className="bbp-field">
              <label>Description</label>
              <textarea
                rows={3}
                value={editPhoto.description ?? ''}
                onChange={(e) => setEditPhoto({ ...editPhoto, description: e.target.value })}
              />
            </div>
            <div className="bbp-field">
              <label>Focal point (what this photo sells)</label>
              <input
                placeholder="e.g. Brass ladder, maple ceiling, drawer storage"
                value={editPhoto.focal_point ?? ''}
                onChange={(e) => setEditPhoto({ ...editPhoto, focal_point: e.target.value || null })}
              />
            </div>
            <div className="bbp-field">
              <label>Price (USD, optional)</label>
              <input
                type="number"
                min={0}
                value={editPhoto.price_usd ?? ''}
                onChange={(e) =>
                  setEditPhoto({
                    ...editPhoto,
                    price_usd: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="bbp-field">
              <label>Option wizard step</label>
              <select
                value={editPhoto.option_category ?? ''}
                onChange={(e) =>
                  setEditPhoto({
                    ...editPhoto,
                    option_category: (e.target.value || null) as BlissBasicOptionCategoryId | null,
                    is_hero: e.target.value ? false : editPhoto.is_hero,
                  })
                }
              >
                <option value="">— Hero / gallery only —</option>
                {BLISS_BASIC_OPTION_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.step}. {c.subtitle}
                  </option>
                ))}
              </select>
            </div>
            <div className="bbp-field">
              <label>Package</label>
              <select
                value={editPhoto.product_id ?? ''}
                onChange={(e) =>
                  setEditPhoto({
                    ...editPhoto,
                    product_id: e.target.value || null,
                  })
                }
              >
                <option value="">— None —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="bbp-field">
              <label>
                <input
                  type="checkbox"
                  checked={editPhoto.in_stock ?? true}
                  onChange={(e) => setEditPhoto({ ...editPhoto, in_stock: e.target.checked })}
                />{' '}
                In stock (shown in customer options wizard)
              </label>
            </div>
            <div className="bbp-field">
              <label>
                <input
                  type="checkbox"
                  checked={editPhoto.active}
                  onChange={(e) => setEditPhoto({ ...editPhoto, active: e.target.checked })}
                />{' '}
                Published on site
              </label>
            </div>
            <div className="bbp-actions">
              <button type="button" className="bbp-btn bbp-btn-primary" onClick={() => void savePhotoEdit()}>
                Save
              </button>
              <button type="button" className="bbp-btn" onClick={() => setEditPhoto(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {editProduct && (
        <div className="bbp-modal-backdrop" role="presentation" onClick={() => setEditProduct(null)}>
          <div className="bbp-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{editProduct === 'new' ? 'New package' : 'Edit package'}</h3>
            {editProduct === 'new' ? (
              <div className="bbp-field">
                <label htmlFor="bbp-new-product-name">Name</label>
                <input id="bbp-new-product-name" />
              </div>
            ) : (
              <>
                <div className="bbp-field">
                  <label>Name</label>
                  <input
                    value={editProduct.name}
                    onChange={(e) => setEditProduct({ ...editProduct, name: e.target.value })}
                  />
                </div>
                <div className="bbp-field">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={editProduct.description ?? ''}
                    onChange={(e) => setEditProduct({ ...editProduct, description: e.target.value })}
                  />
                </div>
                <div className="bbp-field">
                  <label>Price label</label>
                  <input
                    value={editProduct.price_label ?? ''}
                    onChange={(e) => setEditProduct({ ...editProduct, price_label: e.target.value })}
                  />
                </div>
                <div className="bbp-field">
                  <label>Price (USD)</label>
                  <input
                    type="number"
                    value={editProduct.price_usd ?? ''}
                    onChange={(e) =>
                      setEditProduct({
                        ...editProduct,
                        price_usd: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="bbp-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editProduct.active}
                      onChange={(e) => setEditProduct({ ...editProduct, active: e.target.checked })}
                    />{' '}
                    Active
                  </label>
                </div>
              </>
            )}
            <div className="bbp-actions">
              <button type="button" className="bbp-btn bbp-btn-primary" onClick={() => void saveProduct()}>
                Save
              </button>
              <button type="button" className="bbp-btn" onClick={() => setEditProduct(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

